import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
	Box,
	Button,
	Paper,
	Typography,
	CircularProgress,
	Select,
	MenuItem,
	Pagination,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "redux-thunk";

import {
	fetchData,
	FetchDataFailureAction,
	FetchDataSuccessAction,
} from "../../../store/actions";
import { useCustomToast } from "../../Methods/SnackBar";
import { getProjectBreachData, jiraChartResponseProfile } from "../../../Service/Apis";
import {
	AreaChart,
	Area,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
	BarChart,
	Bar,
} from "recharts";
import MonthButtonGroup from "../../utilization/MonthsButtonGroup";
import { jiraChartBlobDownload } from "../chartExport";
import { RootState } from "../../../store/Store";
import ProjectSprintFilter from "../ProjectFilter/ProjectSprintFilter";

const CHART_HEIGHT = 400;
const PROJECT_COLORS: { [key: string]: string } = {};
const ITEMS_PER_PAGE = 5;

const getProjectColor = (projectName: string) => {
	if (!PROJECT_COLORS[projectName]) {
		PROJECT_COLORS[projectName] = `#${Math.floor(
			Math.random() * 16777215
		).toString(16)}`;
	}
	return PROJECT_COLORS[projectName];
};

const CustomTooltip = ({ active, payload, label }: any) => {
	if (!active || !payload?.length) return null;

	// Sort payload by value (desc) then by name (alphabetically)
	const sortedPayload = [...payload].sort((a, b) => {
		const valueA = parseFloat(a?.value?.toString()) || 0;
		const valueB = parseFloat(b?.value?.toString()) || 0;
		
		// First sort by value (descending)
		if (valueB !== valueA) {
			return valueB - valueA;
		}
		
		// Then sort alphabetically by name
		const nameA = String(a?.name || "").toLowerCase();
		const nameB = String(b?.name || "").toLowerCase();
		return nameA.localeCompare(nameB);
	});

	return (
		<div style={{
			backgroundColor: "#ffffff",
			border: "1px solid #e0e0e0",
			borderRadius: "8px",
			padding: "12px 16px",
			boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
			maxHeight: "300px",
			overflowY: "auto",
			minWidth: "200px",
			maxWidth: "350px",
			zIndex: 9999,
		}}>
			<div style={{
				fontSize: "13px",
				fontWeight: 600,
				color: "#1a1a1a",
				marginBottom: "6px",
				borderBottom: "1px solid #eee",
				paddingBottom: "6px",
			}}>
				{label}
			</div>
			{sortedPayload.map((entry: any, index: number) => (
				<div key={index} style={{
					display: "flex",
					alignItems: "center",
					gap: "6px",
					padding: "2px 0",
					fontSize: "12px",
					color: "#333",
				}}>
					<span style={{
						width: "8px",
						height: "8px",
						borderRadius: "50%",
						backgroundColor: entry.color,
						flexShrink: 0,
					}} />
					<span style={{ flex: 1 }}>{entry.name}</span>
					<span style={{ fontWeight: 600 }}>
						{entry.value}
					</span>
				</div>
			))}
		</div>
	);
};

export type ProjectBreachChartProps = {
	projectId?: string;
	embedded?: boolean;
};

const ProjectBreachChart: React.FC<ProjectBreachChartProps> = ({
	projectId,
	embedded = false,
}) => {
	const dispatch2: ThunkDispatch<
		RootState,
		null,
		FetchDataSuccessAction | FetchDataFailureAction
	> = useDispatch();
	const [breachData, setBreachData] = useState<any>([]);
	const [selectedFormat, setSelectedFormat] = useState<string>("excel");
	const [month, setMonth] = useState<string>("1");
	const [loading, setLoading] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const showToast = useCustomToast();
	const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
	const [selectedSprints, setSelectedSprints] = useState<string[]>([]);
	const [page, setPage] = useState(1);

	const nrole = useSelector((state: any) => state.auth.selectedRole);
	const paramsData = useMemo(() => {
		const p: Record<string, string> = { ...jiraChartResponseProfile, role: nrole ?? "Employee",};
		if (projectId) {
			p.project_id = projectId;
		}
		return p;
	}, [nrole, projectId]);

	const fetchBreachData = useCallback(
		async (selectedMonth: string) => {
			try {
				setLoading(true);
				const res = await dispatch2(
					fetchData(`${getProjectBreachData}${selectedMonth}`, paramsData)
				);
				setMonth(selectedMonth);
				setBreachData(Array.isArray(res) ? res : []);
			} catch (error) {
				console.error(error);
			} finally {
				setLoading(false);
			}
		},
		[dispatch2, paramsData]
	);

	useEffect(() => {
		fetchBreachData("1");
	}, [fetchBreachData]);

	const handleMonthChange = (selectedMonth: string) => {
		fetchBreachData(selectedMonth);
	};

	const buttonData = [
		{ label: "1M", onClick: () => handleMonthChange("1") },
		{ label: "3M", onClick: () => handleMonthChange("3") },
		{ label: "6M", onClick: () => handleMonthChange("6") },
		{ label: "1Y", onClick: () => handleMonthChange("12") },
	];

	const onDownload = async () => {
		try {
			setDownloading(true);
			const ext =
				selectedFormat === "excel" ? "xlsx" : selectedFormat;
			await jiraChartBlobDownload(
				`${getProjectBreachData}${month}`,
				{ ...paramsData, download: selectedFormat } as Record<
					string,
					unknown
				>,
				`project_breach.${ext}`,
				showToast
			);
		} finally {
			setDownloading(false);
		}
	};

	const transformDataForChart = (data: any[]) => {
		// Extract all unique sprint names
		const allSprints = new Set<string>();
		data.forEach((project) => {
			project.sprints.forEach((sprint: any) => {
				allSprints.add(sprint.sprint_name);
			});
		});

		// Create data points for each sprint
		return Array.from(allSprints).map((sprintName) => {
			const dataPoint: any = { sprint_name: sprintName };
			data.forEach((project) => {
				const sprint = project.sprints.find(
					(s: any) => s.sprint_name === sprintName
				);
				if (sprint) {
					dataPoint[project.project_name] = sprint.breach_count || 0;
				}
			});
			return dataPoint;
		});
	};

	const getAvailableProjects = (data: any[]) => {
		return data.map((project) => project.project_name);
	};

	const getAvailableSprints = (data: any[]) => {
		const sprints = new Set<string>();
		data.forEach((project) => {
			project.sprints.forEach((sprint: any) => {
				sprints.add(sprint.sprint_name);
			});
		});
		return Array.from(sprints);
	};

	useEffect(() => {
		if (breachData.length > 0) {
			const projects = getAvailableProjects(breachData);
			const sprints = getAvailableSprints(breachData);
			setSelectedProjects(projects);
			setSelectedSprints(sprints);
		}
	}, [breachData]);

	const filteredData = useMemo(() => {
		return breachData
			.filter((project: any) => selectedProjects.includes(project.project_name))
			.map((project: any) => ({
				...project,
				sprints: project.sprints.filter((sprint: any) =>
					selectedSprints.includes(sprint.sprint_name)
				),
			}));
	}, [breachData, selectedProjects, selectedSprints]);

	const transformedData = useMemo(
		() => transformDataForChart(filteredData),
		[filteredData]
	);

	const paginatedData = useMemo(() => {
		const start = (page - 1) * ITEMS_PER_PAGE;
		return transformedData.slice(start, start + ITEMS_PER_PAGE);
	}, [transformedData, page]);

	const handlePageChange = (
		event: React.ChangeEvent<unknown>,
		value: number
	) => {
		setPage(value);
	};

	const chartAreas = useMemo(() => {
		return selectedProjects.map((projectName: string) => (
			<Area
				key={projectName}
				type="monotone"
				dataKey={projectName}
				name={projectName}
				fill={getProjectColor(projectName)}
				stroke={getProjectColor(projectName + "_stroke")}
			/>
		));
	}, [selectedProjects]);

	const chartBars = useMemo(() => {
		return selectedProjects.map((projectName: string) => (
			<Bar
				key={projectName}
				dataKey={projectName}
				name={projectName}
				fill={getProjectColor(projectName)}
				maxBarSize={100}
				barSize={100}
			/>
		));
	}, [selectedProjects]);

	return (
		<Paper sx={embedded ? { m: 0, mb: 2, p: 2 } : { m: 2, p: 4 }}>
			<Box display="flex" justifyContent="space-between" alignItems="center">
				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					flexDirection="column"
					gap={2}
				>
					<Typography variant="h4">Project Breached Dates Analysis</Typography>
					<MonthButtonGroup buttonData={buttonData} />
				</Box>

				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					flexDirection="column"
					gap={2}
				>
					{!projectId ? (
						<Box mb={3}>
							<ProjectSprintFilter
								projects={getAvailableProjects(breachData)}
								selectedProjects={selectedProjects}
								sprints={getAvailableSprints(breachData)}
								selectedSprints={selectedSprints}
								onProjectChange={setSelectedProjects}
								onSprintChange={setSelectedSprints}
								projectData={breachData}
							/>
						</Box>
					) : null}
					{downloading ? (
						<Box
							display="flex"
							justifyContent="center"
							alignItems="center"
							gap={2}
						>
							<CircularProgress size="1rem" />
							<Typography>Downloading...</Typography>
						</Box>
					) : (
						<Button
							variant="outlined"
							size="small"
							onClick={onDownload}
						>
							Export
						</Button>
					)}
				</Box>
			</Box>

			{loading ? (
				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					height={400}
				>
					<CircularProgress />
				</Box>
			) : (
				<>
					<ResponsiveContainer width="100%" height={CHART_HEIGHT}>
						<BarChart
							data={paginatedData}
							margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
							barGap={5}
							barCategoryGap={20}
						>
							<CartesianGrid strokeDasharray="3 3" />
							<XAxis dataKey="sprint_name" height={20} />
							<YAxis />
							<Tooltip
								content={<CustomTooltip />}
								wrapperStyle={{ zIndex: 9999, pointerEvents: "auto" }}
							/>
							<Legend
								verticalAlign="bottom"
								height={36}
								wrapperStyle={{
									paddingTop: "10px",
									bottom: 0,
								}}
							/>
							{chartBars}
						</BarChart>
					</ResponsiveContainer>

					<Box display="flex" justifyContent="center" mt={2}>
						<Pagination
							count={Math.ceil(transformedData.length / ITEMS_PER_PAGE)}
							page={page}
							onChange={handlePageChange}
							color="primary"
							size="large"
						/>
					</Box>
				</>
			)}
		</Paper>
	);
};

export default ProjectBreachChart;
